import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocalStorage } from '@ai-tutor/ui';

/**
 * localStorage-backed practice session that survives sub-tab remounts AND
 * full page reloads (unlike component-local state, which the TabbedInterface
 * unmount destroys).
 *
 * Every session carries a content `signature`; when the underlying artifact
 * is regenerated the signature changes and the stored session is reset (so we
 * never restore stale answers against new questions). The reset only runs once
 * the content is `ready` (non-empty), so the transient loading-empty set
 * doesn't wipe a valid saved session.
 */
export function usePersistentSession<T extends { signature: string }>(
  storageKey: string,
  makeFresh: (signature: string) => T,
  signature: string,
  ready: boolean
): readonly [T, (patch: Partial<T>) => void, () => void] {
  // Compute the initial value once (avoids re-running makeFresh — which may
  // mint a uuid — on every render). If localStorage has a value it wins anyway.
  const initialRef = useRef<T | null>(null);
  if (initialRef.current === null) initialRef.current = makeFresh(signature);

  const [stored, setSession] = useLocalStorage<T>(
    storageKey,
    initialRef.current
  );

  // Normalize a rehydrated value against the current defaults so a session
  // persisted by an OLDER build (missing a field added since) doesn't render
  // as `undefined` and crash. Stored values win over defaults for keys they
  // have; new keys fall back to the fresh default.
  const session = useMemo<T>(
    () => ({ ...(initialRef.current as T), ...stored }),
    [stored]
  );

  // Mirror the latest value so `update` merges against it, not a stale
  // render-time closure (finish() patches `completed` then `submission` in
  // sequence before a re-render). Also stays current with cross-tab writes.
  const latest = useRef(session);
  latest.current = session;

  useEffect(() => {
    if (!ready || !signature) return;
    if (latest.current.signature !== signature) {
      const fresh = makeFresh(signature);
      latest.current = fresh;
      setSession(fresh);
    }
    // Intentionally keyed only on the signal that should trigger a reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, signature]);

  const update = useCallback(
    (patch: Partial<T>) => {
      const next = { ...latest.current, ...patch };
      latest.current = next;
      setSession(next);
    },
    [setSession]
  );

  const reset = useCallback(() => {
    const fresh = makeFresh(signature);
    latest.current = fresh;
    setSession(fresh);
    // makeFresh is a stable module fn; signature is the only real dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, setSession]);

  return [session, update, reset] as const;
}
